import { useEffect, useState } from "react";
import type { ViewContext } from "../../types";
import { parseMark, readReturn, serializeMark, withoutReturn, type PaymentMark, type ReturnState } from "./stripeReturn";

// The payment pages' return handling, in one place: the page's own address
// for Stripe to come back to, the return read once from the address (and
// then dropped from it, so a reload doesn't repeat it), and the "paid, not
// showing yet" mark that keeps Pay hidden for this record in this tab while
// the API catches up (re-read every few seconds for a minute, then by hand).

export const CONFIRM_POLL_MS = 3000;
export const CONFIRM_WINDOW_MS = 60_000;

/** The page's own address on this host, with the link query. */
export function pageUrl(ctx: ViewContext): string {
  return `${window.location.origin}${ctx.links.page(window.location.pathname)}`;
}

function read(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch {
    // Private mode: the mark then lasts as long as the page.
  }
}

export type StripeReturn<B> = {
  /** What the address came back with (read once). */
  returned: ReturnState;
  /** The query as it came back, before the return's keys were dropped. */
  returnSearch: string;
  /** Set once the customer has paid (here, or by a return that says so):
   * Pay stays hidden until the record shows it. */
  paid: PaymentMark<B> | null;
  /** Whether the page should still re-read on its own. */
  polling: boolean;
  /** The customer paid in the page. */
  markPaid: (baseline: B | null) => void;
  /** A payment is about to leave the page (a redirect, a hosted Checkout):
   * what the record looked like, for when it comes back. */
  rememberStart: (baseline: B | null) => void;
  forgetStart: () => void;
  /** The record shows the payment: drop the stored mark. */
  settle: () => void;
};

/** `record` names the page and its record ("secure-payment:o1"). */
export function useStripeReturn<B = null>(
  record: string,
  parseBaseline: (value: unknown) => B | null = () => null,
): StripeReturn<B> {
  const markKey = `baluu:paid:${record}`;
  const startKey = `baluu:paying:${record}`;
  const [returnSearch] = useState(() => (typeof window === "undefined" ? "" : window.location.search));
  const [returned] = useState<ReturnState>(() => readReturn(returnSearch));
  const [paid, setPaid] = useState<PaymentMark<B> | null>(() => {
    if (typeof window === "undefined") return null;
    const now = Date.now();
    if (returned.outcome === "paid") {
      const start = parseMark(read(startKey), now, parseBaseline);
      return { at: now, baseline: start?.baseline ?? parseMark(read(markKey), now, parseBaseline)?.baseline ?? null };
    }
    return parseMark(read(markKey), now, parseBaseline);
  });
  const [timedOut, setTimedOut] = useState(false);

  // Keep the mark for a reload in this tab; the start is spent.
  useEffect(() => {
    if (!paid) return;
    write(markKey, serializeMark(paid.baseline, paid.at));
    write(startKey, null);
  }, [paid, markKey, startKey]);

  // Drop the return's keys from the address.
  useEffect(() => {
    if (returned.outcome === null) return;
    const rest = withoutReturn(window.location.search);
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${rest}${window.location.hash}`);
  }, [returned]);

  useEffect(() => {
    if (!paid) return;
    const timer = window.setTimeout(() => setTimedOut(true), CONFIRM_WINDOW_MS);
    return () => window.clearTimeout(timer);
  }, [paid]);

  return {
    returned,
    returnSearch,
    paid,
    polling: !!paid && !timedOut,
    markPaid: (baseline) => {
      setTimedOut(false);
      setPaid({ at: Date.now(), baseline });
    },
    rememberStart: (baseline) => write(startKey, serializeMark(baseline, Date.now())),
    forgetStart: () => write(startKey, null),
    settle: () => write(markKey, null),
  };
}
